import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Exception } from '../models/exception.model';
import { NgZone } from '@angular/core';
import { GridReadyEvent, ColDef, GridApi, IServerSideDatasource, RowModelType, GridOptions, GridPreDestroyedEvent, GetRowIdFunc, GetRowIdParams, IServerSideGetRowsParams, SortModelItem } from 'ag-grid-community';
import { IconCellRendererComponent } from '../icon-cell-renderer/icon-cell-renderer.component';
import 'ag-grid-enterprise';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ShipmentListsModalComponent } from '../shipment-lists/shipment-lists-modal/shipment-lists-modal.component';

@Component({
  selector: 'app-exception-reports',
  templateUrl: './exception-reports.component.html',
  styleUrls: ['./exception-reports.component.scss']
})
export class ExceptionReportsComponent {
  private apiUrl = '/api/exception_reports/exception_reports.php';
  
  public rowData?: Exception[];
  public themeClass: string ="ag-theme-ulaap";
  public rowModelType: RowModelType = 'serverSide';
  public cacheBlockSize = 100;
  public cacheOverflowSize = 2;
  public maxConcurrentDatasourceRequests = 2;
  public infiniteInitialRowCount = 1;
  public gridVisible = true;
  public gridOptions: GridOptions = {}
  private gridApi!: GridApi<Exception>;

  public getRowId: GetRowIdFunc = (params: GetRowIdParams) => {
    return params.data.barcode;
  }

  constructor(private http: HttpClient, private fb: FormBuilder,  private dialog: MatDialog, private zone: NgZone){}

  public defaultColDef: ColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
      buttons: ['apply', 'clear', 'reset']
    },
    autoHeight: true,
    resizable: true
  }
  
  public columnDefs: ColDef[] = [
    { field: 'barcode', headerName: 'Barcode', pinned: "left", lockPinned: true},
    { field: 'product_uuid', headerName: 'UUID', hide: true},
    { field: 'store_number', headerName: 'Store Num.'},
    { field: 'received_in', headerName: 'Received In',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['true', 'null'],
        filterOptions: ['equals','notEquals'],
      },
      cellRenderer: IconCellRendererComponent,
      cellRendererParams: {
        icon: 'fas fa-square-check',
        color: 'green'
      }
    },
    { field: 'audited', headerName: 'Audited',
      cellRenderer: IconCellRendererComponent,
      cellRendererParams: {
        icon: 'fas fa-square-check',
        color: 'green'
      },
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['true', 'null'],
        filterOptions: ['equals','notEquals'],
      },
    },
    { field: 'return_status', headerName: 'Return Status'},
    { field: 'carton_resolution', headerName: 'Carton Resolution'},
    { field: 'delivery_date', headerName: 'Delivery Date', filter: 'agDateColumnFilter',
    },
    { field: 'delivery_status', headerName: 'Delivery Status', filter: false},
    { field: 'inbound_date', filter: 'agDateColumnFilter', headerName: 'Inbound Date'},
    { field: 'asn_number', headerName: 'ASN Number'},
    { field: 'trip_number', headerName: 'Trip Number'},
    { field: 'trailer_id', headerName: 'Trailer ID'},
    { field: 'week_no', headerName: 'Week No.', filter: 'agNumberColumnFilter'},
    { field: 'customer_name', headerName: 'Customer'},
    { field: 'external_bill_of_lading', headerName: 'External BOL'},
    {
      headerName: 'Action', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-exchange'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.moveException(params);
          });
        });
        return button;
      }, editable: false, width: 100,
    },
  ]

  reports_form: FormGroup = this.fb.group({
    asn: [''],
    customer: [''],
    date_from: [''],
    date_to: [''],
    week_from: [''],
    week_to: [''],
    barcode: [''],    
  })

  
  // changes column names in filterModel for columns that appear in multiple tables referenced in SQL query
  // also switches date values for bad date ranges (dateTo <--> dateFrom)
  private translateFilters(filterModel: any): any {
    const aliasMapping: {[key: string]: string} = {
      "store_number": "p.store_number",
      "return_status": "p.return_status",
      "customer_name": "l.customer_name",
      /*"delivery_date": "lst.delivery_date",*/
      "asn_number": "sl.asn_number",
      "inbound_date": "sl.date",
      "trip_number": "sl.trip_number",
      "external_bill_of_lading": "sl.external_bill_of_lading"
    };

    const translatedFilters: { [key: string]: any} = {};
    for (const alias in filterModel) {
      // switches date values if needed
      if(filterModel[alias].type === 'inRange' && filterModel[alias].filterType === 'date'){
        let startDate = new Date(filterModel[alias].dateFrom);
        let endDate = new Date(filterModel[alias].dateTo);
        if(startDate > endDate){
          const dateHolder = filterModel[alias].dateTo;
          filterModel[alias].dateTo = filterModel[alias].dateFrom;
          filterModel[alias].dateFrom = dateHolder;
        }
      }
      // switch number values if needed
      if(filterModel[alias].type === 'inRange' && filterModel[alias].filterType === 'number' && filterModel[alias].filterTo < filterModel[alias].filter){
        const numHolder = filterModel[alias].filterTo;
        filterModel[alias].filterTo = filterModel[alias].filter;
        filterModel[alias].filter = numHolder;
      }
      // move filterModel data into new filter model with updated alias
      const qualifiedName: string = aliasMapping[alias] || alias;
      translatedFilters[qualifiedName] = filterModel[alias];
    }
    return translatedFilters;
  }

  openDeliveryReports(){
    window.open('/delivery_reports');
  }

  openStoreReports(){
    window.open('/store_reports');
  }

  // When toggle button value is changed, reset the form
  onToggleFilterChange(event: any){
    if(event.value !== 'asn')
      this.reports_form.get('asn')?.setValue('');
    if(event.value !== 'date'){
      this.reports_form.get('date_from')?.setValue('');
      this.reports_form.get('date_to')?.setValue('');
    }
    if(event.value !== 'customer')
      this.reports_form.get('customer')?.setValue('');
    if(event.value !== 'barcode')
      this.reports_form.get('barcode')?.setValue('');
  }

  search(){
    let model = this.gridApi.getFilterModel();
    if(this.reports_form.get('asn')?.value){
      const asn_filter = {
        "asn_number": {
          "filterType": "text",
          "type": "contains",
          "filter": this.reports_form.get('asn')?.value
        }
      }
      model = Object.assign({}, asn_filter, model);
    }
    else if(this.reports_form.get('date_from')?.value){
      const date_filter = {
        "inbound_date": {
          "filterType": "date",
          "type": "inRange",
          "dateFrom": this.formatDateAsYYYYMMDD(this.reports_form.get('date_from')?.value),
          "dateTo": this.formatDateAsYYYYMMDD(this.reports_form.get('date_to')?.value),
        }
      }
      model = Object.assign({}, date_filter, model);
    }
    else if(this.reports_form.get('barcode')?.value){
      const barcode_filter = {
        "barcode": {
          "filterType": "text",
          "type": "contains",
          "filter": this.reports_form.get('barcode')?.value
        }
      };
      model = Object.assign({}, barcode_filter, model);
    }
    else if(this.reports_form.get('week_from')){
      const week_filter = {
        "week_no": {
          "filterType": "number",
          "type": "inRange",
          "filter": parseInt(this.reports_form.get('week_from')?.value ?? "0"),
          "filterTo": parseInt(this.reports_form.get('week_to')?.value ?? "52"),
        }
      }
      model = Object.assign({}, week_filter, model);
    }
    console.log(model);
    this.gridApi.setFilterModel(model);
  }

  resetFilters(){
    this.gridApi.setFilterModel({});
    this.reports_form.get('asn')?.setValue("");
    this.reports_form.get('week_from')?.setValue("");
    this.reports_form.get('week_to')?.setValue("");
    this.reports_form.get('barcode')?.setValue("");
    this.reports_form.get('date_from')?.setValue("");
    this.reports_form.get('date_to')?.setValue("");
  }

  onGridReady(params: GridReadyEvent<Exception>){
    const dataSource: IServerSideDatasource = {
      getRows: (params: IServerSideGetRowsParams<any, any> & { sortModel: SortModelItem[], filterModel: any}) => {
        const sortModel = params.request.sortModel;
        let filterModel = params.request.filterModel;
        filterModel = this.translateFilters(filterModel); 
        
        const url = this.buildUrl(params.request.startRow || 0, params.request.endRow || 0, sortModel, filterModel);
        this.gridApi = params.api;

        this.http.get<{ data: {data: Exception[]}, totalRows: number}>(url).subscribe(
          response => {

            if(Array.isArray(response.data.data)){
              params.success({ rowData: response.data.data, rowCount: response.totalRows})
            }
            else{
              console.log('response.data.data is not an array, it is a...' + (typeof response.data))
              console.log(JSON.stringify(response))
            }
          },
          error => {
            console.error('error getting exception report data: ', error);
            params.fail();
          }
        )
      }
    }
    params.api.setGridOption('serverSideDatasource', dataSource);  
  }

  private buildUrl(startRow: number, endRow: number, sortModel: SortModelItem[], filterModel: any): string {
    // Construct the URL with query parameters for startRow, endRow, filters, and sortModel
    const filters = encodeURIComponent(JSON.stringify(filterModel));
    const sort = encodeURIComponent(JSON.stringify(sortModel));
    return `${this.apiUrl}?startRow=${startRow}&endRow=${endRow}&filters=${filters}&sortModel=${sort}`;
  }

  ngOnInit(){}

  exportToCSV() {
    var params = { columnSeparator: "|"}
    this.gridApi.exportDataAsCsv(params);
  }

  exportToExcel(){
    this.gridApi.exportDataAsExcel();
  }

  toggleTheme(){
    this.themeClass = this.themeClass === 'ag-theme-ulaap' ? 'ag-theme-ulaap-dark' : 'ag-theme-ulaap';
  }

  formatDateAsYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  moveException(params: any) {
    const dialogConfig = new MatDialogConfig();
    const product_uuid = params.data.product_uuid;
    const barcode = params.data.barcode;
    const asn_number = params.data.asn_number;
    const trip_number = params.data.trip_number;
    const store_number = params.data.store_number;

    dialogConfig.width = '10'; // You can use pixels or other units
    dialogConfig.height = '10';
    dialogConfig.data = {
      data: {
        modalTitle: 'Move Exception Carton',
        modalMessage: {product_uuid: product_uuid, barcode: barcode, asn_number: asn_number, trip_number: trip_number, store_number: store_number},
      },
    };

    console.log('Dialog Config:', dialogConfig);

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }
}